import { Nullable } from '@types'

import { User } from '@modules/auth/hooks/types'
import { ApiFilterItem, CustomColumnType, ShortListCustomField } from '@modules/shortlist/types'

import {
  PendoCustomListAddEventType,
  PendoCustomListBulkEventType,
  PendoCustomListFilterEventType,
  PendoCustomListMoveOrCopyEventType,
} from '../types'
import { getEventDefaultInfo } from '../utils'

export const prepareMoveOrCopyEventData = ({
  user,
  location,
  eventType,
  profileTypes,
  originalCustomListId,
  destinationCustomListIds,
}: {
  eventType: PendoCustomListMoveOrCopyEventType
  location?: string
  user?: User | null
  profileTypes?: string[]
  originalCustomListId?: number
  destinationCustomListIds?: number[]
}) => {
  return {
    ...getEventDefaultInfo(user, location),
    eventType,
    profileTypes,
    originalCustomListId,
    destinationCustomListIds,
  }
}

export const preparePendoEditCustomListEvent = ({
  eventType,
  customListId,
  user,
}: {
  eventType: string
  customListId?: number | null
  user?: User | null
}) => {
  return {
    ...getEventDefaultInfo(user),
    location: 'custom-lists-page',
    eventType,
    customListId,
  }
}

export const prepareCustomListFilterEvent = ({
  eventType,
  customListId,
  user,
  filterRowData,
}: {
  eventType: PendoCustomListFilterEventType
  customListId?: number | null
  user?: User | null
  filterRowData?: ApiFilterItem
}) => {
  const actionMap: Record<PendoCustomListFilterEventType, string> = {
    [PendoCustomListFilterEventType.AddOne]: 'Click on “Add filter”',
    [PendoCustomListFilterEventType.UpdateOne]: 'Update filter value or operator',
    [PendoCustomListFilterEventType.RemoveOne]: 'Click on cross to delete individual row',
    [PendoCustomListFilterEventType.RemoveAll]: 'Click on “Remove all”',
    [PendoCustomListFilterEventType.Close]: 'Click on cross to close the pop-over window',
  }

  return {
    ...getEventDefaultInfo(user),
    location: 'Custom list overview screen',
    eventType: actionMap[eventType],
    filterRowData,
    customListId,
  }
}

export const preparePendoAddCustomListPopUpEvent = ({
  eventType,
  location,
  user,
}: {
  eventType: PendoCustomListAddEventType
  location?: string
  user?: User | null
}) => {
  return {
    ...getEventDefaultInfo(user, location),
    eventType,
  }
}

export const preparePendoCustomListBulkEvent = ({
  location,
  user,
  eventType,
  customListId,
  description,
  profileType,
}: {
  user?: User | null
  location?: string
  eventType: PendoCustomListBulkEventType
  customListId?: number
  description?: string
  profileType?: string
}) => {
  return {
    ...getEventDefaultInfo(user, location),
    description: description || 'Selection of existing custom list',
    customListId,
    eventType,
    profileType,
  }
}

export const preparePendoCustomColumnEvent = ({
  anchorEl,
  customField,
  user,
  eventType,
}: {
  anchorEl: Nullable<HTMLElement>
  customField: ShortListCustomField
  user?: User | null
  eventType: string
}) => {
  return {
    ...getEventDefaultInfo(user),
    location: anchorEl ? 'manage_columns_popover' : 'shortlist_table',
    column_name: customField?.fieldName,
    column_type: customField?.fieldType === CustomColumnType.AUTOCOMPLETE ? 'lookup' : 'text',
    lookup_values: customField?.fieldOptions?.length,
    is_edit: !!customField.id,
    eventType,
  }
}
